// src/homepage/WhyPsychiatrySection.tsx
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {Link} from "react-router-dom";

const Section = styled.section`
    padding: 96px 0;
    position: relative;
    overflow: hidden;
    background: linear-gradient(135deg, #FFFFFF 0%, #F5F5F5 50%, #C0C0C0 100%);

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 1px;
        background: linear-gradient(
                90deg,
                transparent 0%,
                rgba(153, 1, 71, 0.3) 50%,
                transparent 100%
        );
    }
`;

const BackgroundPattern = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0.03;
    background-image: radial-gradient(circle at 25% 25%, #990147 2px, transparent 0),
    radial-gradient(circle at 75% 75%, #990147 2px, transparent 0);
    background-size: 60px 60px;
`;

const Container = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
    position: relative;
    z-index: 2;

    @media (max-width: 768px) {
        padding: 0 1rem;
    }
`;

const HeaderWrapper = styled.div`
    text-align: center;
    margin-bottom: 4rem;
`;

const HeaderTag = styled(motion.span)`
    display: inline-block;
    background: linear-gradient(135deg, #990147 0%, #cc3366 100%);
    color: white;
    padding: 0.6rem 1.6rem;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.5px;
    margin-bottom: 1.2rem;
    box-shadow: 0 6px 20px rgba(153, 1, 71, 0.2);
`;

const SectionTitle = styled(motion.h2)`
    font-size: 2.8rem;
    font-weight: 800;
    background: linear-gradient(135deg, #333333 0%, #990147 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 1.2rem;
    line-height: 1.1;

    @media (max-width: 768px) {
        font-size: 2rem;
    }
`;

const SectionSubtitle = styled(motion.p)`
    font-size: 1rem;
    color: #555555;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.5;
    font-weight: 400;
`;

const ContentGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: start;

    @media (max-width: 968px) {
        grid-template-columns: 1fr;
        gap: 3rem;
    }
`;

const ServicesSection = styled.div`
    position: relative;
`;

const ServiceItem = styled(motion.div)`
    background: rgba(255, 255, 255, 0.9);
    border-radius: 20px;
    padding: 2.5rem;
    margin-bottom: 2rem;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.8);
    position: relative;
    overflow: hidden;
    backdrop-filter: blur(10px);

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        width: 4px;
        background: linear-gradient(135deg, #990147 0%, #cc3366 100%);
    }

    &:last-child {
        margin-bottom: 0;
    }

    &:hover {
        transform: translateY(-5px);
        box-shadow: 0 20px 40px rgba(153, 1, 71, 0.15);
    }

    transition: all 0.3s ease;
`;

const ServiceNumber = styled.div`
    position: absolute;
    top: 1.5rem;
    right: 1.5rem;
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg, #990147 0%, #cc3366 100%);
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 800;
    font-size: 1.2rem;
    box-shadow: 0 5px 15px rgba(153, 1, 71, 0.3);
`;

const ServiceTitle = styled.h3`
    font-size: 1.5rem;
    font-weight: 700;
    background: linear-gradient(135deg, #333333 0%, #990147 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 1rem;
    padding-right: 3rem;
`;

const ServiceDescription = styled.p`
    color: #555555;
    line-height: 1.6;
    font-size: 1rem;
    margin: 0;
`;

const TextContent = styled.div`
    position: relative;
`;

const MainDescription = styled(motion.div)`
    p {
        font-size: 1.1rem;
        color: #555555;
        line-height: 1.7;
        margin-bottom: 2rem;
        font-weight: 400;

        &:last-child {
            margin-bottom: 0;
        }
    }
`;

const HighlightBox = styled(motion.div)`
    background: linear-gradient(135deg, rgba(153, 1, 71, 0.95) 0%, rgba(204, 51, 102, 0.95) 100%);
    color: white;
    padding: 2.5rem;
    border-radius: 20px;
    margin: 2.5rem 0;
    box-shadow: 0 20px 40px rgba(153, 1, 71, 0.3);
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        inset: 0;
        background: radial-gradient(
                circle at 20% 80%,
                rgba(255, 255, 255, 0.1) 0%,
                transparent 50%
        );
        pointer-events: none;
    }

    p {
        font-size: 1.2rem;
        font-weight: 500;
        line-height: 1.6;
        margin: 0;
        font-style: italic;
        position: relative;
        z-index: 2;
        color: #FFFFFF;
        text-align: center;
    }
`;

const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
    margin-top: 2.5rem;

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
    }
`;

const StatItem = styled(motion.div)`
    text-align: center;
    padding: 1.5rem;
    background: rgba(255, 255, 255, 0.8);
    border-radius: 15px;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.5);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.05);

    .number {
        font-size: 2rem;
        font-weight: 800;
        background: linear-gradient(135deg, #990147, #cc3366);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        line-height: 1;
        margin-bottom: 0.5rem;
    }

    .label {
        font-size: 0.85rem;
        color: #555555;
        font-weight: 600;
        line-height: 1.3;
    }
`;

const CTAButton = styled(motion.button)`
    background: linear-gradient(135deg, #990147 0%, #cc3366 100%);
    color: white;
    border: none;
    padding: 1rem 2rem;
    border-radius: 12px;
    font-weight: 700;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-top: 2rem;
    box-shadow: 0 10px 30px rgba(153, 1, 71, 0.3);
    
    &:hover {
        transform: translateY(-3px);
        box-shadow: 0 15px 35px rgba(153, 1, 71, 0.4);
    }
`;

const WhyPsychiatrySection: React.FC = () => {
    const stats = [
        { number: '25%', label: 'ľudí zažije psychické problémy počas života' },
        { number: '2x', label: 'vzrástol výskyt úzkostných porúch za posledných 10 rokov' },
        { number: '80%', label: 'psychických porúch sa dá úspešne liečiť' },
        { number: '50%', label: 'ľudí s psychickými problémami nevyhľadá pomoc' }
    ];

    const services = [
        {
            number: 1,
            title: 'Diagnostika a liečba',
            description: 'Presná diagnostika je základom účinnej liečby. Prostredníctvom dôkladného vyšetrenia a hodnotenia identifikujeme konkrétne psychické problémy a navrhujeme individualizovaný liečebný plán zameraný na trvalé zlepšenie zdravotného stavu.'
        },
        {
            number: 2,
            title: 'Farmakoterapia',
            description: 'Moderná lieková terapia ponúka bezpečné a účinné riešenia pre rôzne psychické poruchy. Výber liekov vždy prispôsobujeme individuálnym potrebám pacienta s dôrazom na minimalizáciu vedľajších účinkov a maximálny liečebný benefit.'
        },
        {
            number: 3,
            title: 'Psychoterapia',
            description: 'Psychoterapia poskytuje priestor pre pochopenie a zvládanie emócií, myšlienok a správania. Prostredníctvom rozhovoru a terapeutických techník pomáhame pacientom nájsť účinné stratégie pre zvládanie ťažkostí a dosiahnutie osobného rastu.'
        },
        {
            number: 4,
            title: 'Podporná starostlivosť',
            description: 'Komplexná podporná starostlivosť zahŕňa pravidelné sledovanie stavu, úpravu liečby podľa potreby a pomoc pri reintegrácii do bežného života. Našim cieľom je poskytovať kontinuálnu podporu na ceste k trvalému duševnému zdraviu.'
        }
    ];

    return (
        <Section id="psychiatria">
            <BackgroundPattern />
            <Container>
                <HeaderWrapper>
                    <HeaderTag
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                    >
                        Prečo psychiatria?
                    </HeaderTag>
                    <SectionTitle
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        viewport={{ once: true }}
                    >
                        Duševné zdravie je základ kvalitného života
                    </SectionTitle>
                    <SectionSubtitle
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        viewport={{ once: true }}
                    >
                        Psychiatria pomáha ľuďom návrat k plnohodnotnému životu a duševnej pohode
                    </SectionSubtitle>
                </HeaderWrapper>

                <ContentGrid>
                    <ServicesSection>
                        {services.map((service, index) => (
                            <ServiceItem
                                key={service.number}
                                initial={{ opacity: 0, x: -50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                whileHover={{ y: -5 }}
                            >
                                <ServiceNumber>{service.number}</ServiceNumber>
                                <ServiceTitle>{service.title}</ServiceTitle>
                                <ServiceDescription>{service.description}</ServiceDescription>
                            </ServiceItem>
                        ))}
                    </ServicesSection>

                    <TextContent>
                        <MainDescription>
                            <motion.p
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                                viewport={{ once: true }}
                            >
                                Psychiatria je medicínsky odbor, ktorý sa zaoberá diagnostikou, liečbou a prevenciou duševných porúch. Rovnako ako iné lekárske špecializácie, psychiatria kombinuje vedecké poznatky s klinickou praxou na pomoc pacientom s psychickými problémami.
                            </motion.p>

                            <motion.p
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                viewport={{ once: true }}
                            >
                                Duševné zdravie je rovnako dôležité ako fyzické. Depresia, úzkosť, bipolárna porucha alebo schizofrénia môžu významne ovplyvniť kvalitu života, prácu, vzťahy a celkovú spokojnosť. Včasná odborná pomoc môže zabrániť zhoršovaniu stavu a umožniť návrat k bežnému životu.
                            </motion.p>

                            <HighlightBox
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.6, delay: 0.3 }}
                                viewport={{ once: true }}
                                whileHover={{ scale: 1.02 }}
                            >
                                <p>
                                    "Vyhľadanie psychiatrickej pomoci nie je známkou slabosti, ale prejavom zodpovednosti voči sebe samému."
                                </p>
                            </HighlightBox>

                            <motion.p
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.4 }}
                                viewport={{ once: true }}
                            >
                                Moderná psychiatria ponúka efektívne liečebné metódy vrátane farmakoterapie, psychoterapie a kombinovaných prístupov. Cieľom nie je len eliminovať príznaky, ale pomôcť pacientom opätovne nájsť radosť zo života, zlepšiť vzťahy a dosiahnuť osobné ciele.
                            </motion.p>

                            <StatsGrid>
                                {stats.map((stat, index) => (
                                    <StatItem
                                        key={stat.label}
                                        initial={{ opacity: 0, y: 30 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{
                                            duration: 0.6,
                                            delay: 0.5 + index * 0.1,
                                        }}
                                        viewport={{ once: true }}
                                        whileHover={{ scale: 1.05, y: -5 }}
                                    >
                                        <div className="number">{stat.number}</div>
                                        <div className="label">{stat.label}</div>
                                    </StatItem>
                                ))}
                            </StatsGrid>


                        </MainDescription>
                    </TextContent>
                </ContentGrid>
            </Container>
        </Section>
    );
};

export default WhyPsychiatrySection;